import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

/**
 * Admin → APIs. A single aggregated Swagger UI is hosted by the gateway
 * (springdoc.swagger-ui.urls lists every container's spec). Rather than one
 * iframe per service, we embed that one aggregated UI and let the tabs switch
 * which definition it shows — entirely client-side: selecting a tab drives
 * Swagger's own (hidden) "Select a definition" <select>, which loads only that
 * service's /v3/api-docs. The topbar is hidden for a clean look, exactly like
 * the old per-service tabs; the tabs replace its dropdown.
 *
 * The iframe is same-origin (served through the gateway / dev proxy), so we can
 * reach into its document to inject the topbar-hide CSS and toggle the select.
 */
const IFRAME_SRC =
  '/gateway/swagger-ui/index.html?docExpansion=none&filter=true&defaultModelsExpandDepth=-1';

// Hide Swagger's own chrome inside the iframe: the topbar (definition dropdown —
// the tabs drive it instead), the big "OpenAPI definition" info block, and the
// Servers block. The api-docs link those sections carried is rendered in the
// parent (above the iframe) instead, so it survives Swagger's re-render on each
// definition switch and tracks the active tab.
const HIDE_CHROME_CSS = [
  '.swagger-ui .topbar { display: none !important; }',
  '.swagger-ui .information-container { display: none !important; }',
  '.swagger-ui .scheme-container { display: none !important; }',
].join('\n');

// Swagger UI ships no official dark theme, but the iframe is same-origin so we
// can inject one. The palette is driven by `--pt-*` CSS vars copied from the
// active app theme (see buildSwaggerVarsCss), so the embedded Swagger MIMICS
// whichever dark theme is active rather than using a fixed palette. Rules only
// bite when <html> carries `ptv2-dark`, so the default light Swagger is
// untouched otherwise.
const DARK_CSS = `
html.ptv2-dark, html.ptv2-dark body, html.ptv2-dark .swagger-ui { color-scheme: dark; background: hsl(var(--pt-bg)); }
html.ptv2-dark .swagger-ui,
html.ptv2-dark .swagger-ui .info .title,
html.ptv2-dark .swagger-ui .opblock-tag,
html.ptv2-dark .swagger-ui label,
html.ptv2-dark .swagger-ui .opblock .opblock-summary-operation-id,
html.ptv2-dark .swagger-ui .opblock .opblock-summary-path,
html.ptv2-dark .swagger-ui .opblock .opblock-summary-description,
html.ptv2-dark .swagger-ui .opblock-description-wrapper p,
html.ptv2-dark .swagger-ui .opblock-external-docs-wrapper p,
html.ptv2-dark .swagger-ui table thead tr td,
html.ptv2-dark .swagger-ui table thead tr th,
html.ptv2-dark .swagger-ui .parameter__name,
html.ptv2-dark .swagger-ui .parameter__type,
html.ptv2-dark .swagger-ui .response-col_status,
html.ptv2-dark .swagger-ui .response-col_description,
html.ptv2-dark .swagger-ui .model,
html.ptv2-dark .swagger-ui .model-title,
html.ptv2-dark .swagger-ui h1, html.ptv2-dark .swagger-ui h2,
html.ptv2-dark .swagger-ui h3, html.ptv2-dark .swagger-ui h4,
html.ptv2-dark .swagger-ui h5,
html.ptv2-dark .swagger-ui .renderedMarkdown p { color: hsl(var(--pt-fg)); }
html.ptv2-dark .swagger-ui .opblock-tag { border-bottom-color: hsl(var(--pt-border)); }
html.ptv2-dark .swagger-ui .opblock { background: transparent; border-color: hsl(var(--pt-border)); box-shadow: none; }
html.ptv2-dark .swagger-ui .opblock .opblock-summary { border-color: hsl(var(--pt-border)); }
html.ptv2-dark .swagger-ui .opblock .opblock-section-header { background: hsl(var(--pt-muted)); box-shadow: none; }
html.ptv2-dark .swagger-ui .opblock .opblock-section-header h4,
html.ptv2-dark .swagger-ui .opblock .opblock-section-header label { color: hsl(var(--pt-fg)); }
html.ptv2-dark .swagger-ui section.models { background: hsl(var(--pt-bg)); border-color: hsl(var(--pt-border)); }
html.ptv2-dark .swagger-ui section.models .model-container,
html.ptv2-dark .swagger-ui .model-box { background: hsl(var(--pt-card)); }
html.ptv2-dark .swagger-ui input[type=text],
html.ptv2-dark .swagger-ui input[type=email],
html.ptv2-dark .swagger-ui input[type=password],
html.ptv2-dark .swagger-ui textarea,
html.ptv2-dark .swagger-ui select { background: hsl(var(--pt-input)); color: hsl(var(--pt-fg)); border-color: hsl(var(--pt-border)); }
html.ptv2-dark .swagger-ui .microlight { background: hsl(var(--pt-bg)); color: hsl(var(--pt-fg)); }
html.ptv2-dark .swagger-ui table tbody tr td { border-color: hsl(var(--pt-border)); color: hsl(var(--pt-fg)); }
html.ptv2-dark .swagger-ui .tab li { color: hsl(var(--pt-fg)); }
html.ptv2-dark .swagger-ui .btn { color: hsl(var(--pt-fg)); border-color: hsl(var(--pt-border)); }
html.ptv2-dark .swagger-ui svg:not(:root) { fill: hsl(var(--pt-muted-fg)); }
html.ptv2-dark .swagger-ui .info a,
html.ptv2-dark .swagger-ui a.nostyle { color: hsl(var(--pt-primary)); }
`;

// The app-theme tokens copied into the iframe so the dark Swagger above mimics
// the active theme. Keyed: iframe var name -> parent app token name.
const SWAGGER_THEME_VARS: Readonly<Record<string, string>> = {
  '--pt-bg': '--background',
  '--pt-fg': '--foreground',
  '--pt-card': '--card',
  '--pt-muted': '--muted',
  '--pt-muted-fg': '--muted-foreground',
  '--pt-border': '--border',
  '--pt-input': '--input',
  '--pt-primary': '--primary',
};

/** Snapshot the active theme's token values as `--pt-*` vars for the iframe. */
function buildSwaggerVarsCss(): string {
  const root = getComputedStyle(document.documentElement);
  const decls = Object.entries(SWAGGER_THEME_VARS)
    .map(([ptVar, token]) => `${ptVar}: ${root.getPropertyValue(token).trim()};`)
    .join(' ');
  return `html.ptv2-dark { ${decls} }`;
}

/**
 * Match the embedded Swagger to the app theme. Any theme whose id ends in
 * `-dark` (glass-dark, gray-dark, …) turns on the dark stylesheet, and the
 * copied `--pt-*` vars make Swagger mimic that specific theme's palette. Light
 * themes drop the class and fall back to Swagger's default light. Re-runs on
 * theme switch, re-snapshotting the vars so the colours track live.
 */
function applySwaggerTheme(doc: Document): void {
  const isDark =
    document.documentElement.dataset.theme?.endsWith('-dark') ?? false;
  doc.documentElement.classList.toggle('ptv2-dark', isDark);
  if (!isDark) return;
  let vars = doc.getElementById('pt-theme-vars');
  if (vars === null) {
    vars = doc.createElement('style');
    vars.id = 'pt-theme-vars';
    doc.head.appendChild(vars);
  }
  vars.textContent = buildSwaggerVarsCss();
}

// `definitionName` must match the gateway's springdoc.swagger-ui.urls[].name values.
const SERVICES = [
  {
    key: 'core',
    labelKey: 'routes.admin.apis.tabs.core',
    definitionName: 'Core',
    apiDocs: '/core/v3/api-docs',
  },
  {
    key: 'vocabularyService',
    labelKey: 'routes.admin.apis.tabs.vocabularyService',
    definitionName: 'Vocabulary Service',
    apiDocs: '/vocabulary-service/v3/api-docs',
  },
  {
    key: 'gateway',
    labelKey: 'routes.admin.apis.tabs.gateway',
    definitionName: 'Gateway',
    apiDocs: '/gateway/v3/api-docs',
  },
] as const;

type ServiceKey = (typeof SERVICES)[number]['key'];

function serviceFor(key: ServiceKey): (typeof SERVICES)[number] {
  return SERVICES.find((s) => s.key === key) ?? SERVICES[0];
}

/**
 * Switch Swagger UI's definition <select> to the named definition and fire the
 * change so Swagger loads that spec. Returns false if the select isn't present
 * yet (Swagger populates it only after fetching its aggregated config).
 */
function selectDefinition(doc: Document, definitionName: string): boolean {
  const select = doc.querySelector<HTMLSelectElement>('.download-url-wrapper select');
  if (!select) return false;
  const option = Array.from(select.options).find((o) => o.text.trim() === definitionName);
  if (!option) return false;
  if (select.value !== option.value) {
    select.value = option.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }
  return true;
}

export function AdminApisPage(): ReactElement {
  const { t } = useTranslation();
  const [tab, setTab] = useState<ServiceKey>('core');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const loadedRef = useRef(false);
  const tabRef = useRef<ServiceKey>(tab);
  tabRef.current = tab;

  // The <select> only exists after Swagger UI has fetched its aggregated config
  // post-load, so poll briefly until it's there.
  const applySelection = useCallback((key: ServiceKey) => {
    const definitionName = serviceFor(key).definitionName;
    let attempts = 0;
    const tick = (): void => {
      let doc: Document | null | undefined;
      try {
        doc = iframeRef.current?.contentDocument;
      } catch {
        return; // cross-origin (shouldn't happen — served same-origin via the gateway)
      }
      if (doc && selectDefinition(doc, definitionName)) return;
      attempts += 1;
      if (attempts < 50) window.setTimeout(tick, 100);
    };
    tick();
  }, []);

  const handleLoad = useCallback(() => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (doc) {
        const style = doc.createElement('style');
        style.textContent = HIDE_CHROME_CSS;
        doc.head.appendChild(style);
        const darkStyle = doc.createElement('style');
        darkStyle.textContent = DARK_CSS;
        doc.head.appendChild(darkStyle);
        applySwaggerTheme(doc);
      }
    } catch {
      // Cross-origin or detached document — leaving the topbar visible is a
      // recoverable failure mode, no need to surface to the user.
    }
    loadedRef.current = true;
    applySelection(tabRef.current);
  }, [applySelection]);

  useEffect(() => {
    if (loadedRef.current) applySelection(tab);
  }, [tab, applySelection]);

  // Keep the embedded Swagger UI's light/dark in step with the app theme,
  // including when the user switches themes while this page is open.
  useEffect(() => {
    const sync = (): void => {
      try {
        const doc = iframeRef.current?.contentDocument;
        if (doc) applySwaggerTheme(doc);
      } catch {
        // cross-origin / detached document — ignore
      }
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Tabs
      value={tab}
      onValueChange={(v) => {
        setTab(v as ServiceKey);
      }}
      className="flex h-full flex-col"
    >
      <TabsList aria-label={t('routes.admin.apis.heading')}>
        {SERVICES.map((service) => (
          <TabsTrigger key={service.key} value={service.key}>
            {t(service.labelKey)}
          </TabsTrigger>
        ))}
      </TabsList>
      {/* The api-docs link that Swagger's hidden info block carried, kept here so it
          tracks the active tab and survives Swagger's re-render on definition switch.
          Sits just above the (hidden) Servers section, i.e. at the top of the panel. */}
      <div className="border-b border-border bg-card px-6 py-2 text-sm">
        <a
          href={serviceFor(tab).apiDocs}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          {serviceFor(tab).apiDocs}
        </a>
      </div>
      <div className="min-h-0 flex-1">
        <iframe
          ref={iframeRef}
          src={IFRAME_SRC}
          title={t('routes.admin.apis.iframeTitle')}
          referrerPolicy="same-origin"
          onLoad={handleLoad}
          className="h-full w-full border-0"
        />
      </div>
    </Tabs>
  );
}
